/* =============================================================================
 * AYA Skeleton — H_UIPOLISH_V1_L3 Palier 1
 * -----------------------------------------------------------------------------
 * Placeholder shimmer "neutre", non destructif. Capitalisé pour ne pas écraser
 * le shadcn `skeleton.jsx` (lowercase) déjà présent.
 *
 * Props:
 *   - variant : "text" | "rect" | "circle" (default "rect")
 *   - width, height : nombre (px) ou string (CSS)
 *   - radius : override radius (sinon défaut selon variant)
 *   - lines : pour variant "text", nombre de lignes (default 1)
 *   - className, style : forwardés
 *
 * Le shimmer respecte prefers-reduced-motion (cf. motion.css).
 * ========================================================================== */

import React from "react";

function dim(v, fallback) {
    if (v == null) return fallback;
    if (typeof v === "number") return `${v}px`;
    return v;
}

function baseSurface(radius) {
    return {
        display: "block",
        backgroundColor: "var(--aya-surface-sunken)",
        borderRadius: radius,
        overflow: "hidden",
        position: "relative",
    };
}

function ShimmerOverlay() {
    return (
        <span
            aria-hidden="true"
            className="aya-motion-shimmer"
            style={{
                position: "absolute",
                inset: 0,
                pointerEvents: "none",
            }}
        />
    );
}

export function AyaSkeleton({
    variant = "rect",
    width,
    height,
    radius,
    lines = 1,
    className,
    style,
    ...rest
}) {
    if (variant === "text") {
        const lineHeight = dim(height, "12px");
        const itemWidth = dim(width, "100%");
        return (
            <div
                data-testid="aya-skeleton-text"
                className={className}
                style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "var(--aya-space-2)",
                    width: itemWidth,
                    ...style,
                }}
                {...rest}
            >
                {Array.from({ length: Math.max(1, lines) }).map((_, i) => {
                    const isLast = i === lines - 1 && lines > 1;
                    return (
                        <span
                            key={i}
                            aria-hidden="true"
                            style={{
                                ...baseSurface(
                                    radius || "var(--aya-radius-xs)"
                                ),
                                height: lineHeight,
                                width: isLast ? "72%" : "100%",
                            }}
                        >
                            <ShimmerOverlay />
                        </span>
                    );
                })}
            </div>
        );
    }

    if (variant === "circle") {
        const size = dim(width || height, "40px");
        return (
            <span
                data-testid="aya-skeleton-circle"
                role="presentation"
                aria-hidden="true"
                className={className}
                style={{
                    ...baseSurface(
                        radius || "var(--aya-radius-full)"
                    ),
                    width: size,
                    height: dim(height || width, "40px"),
                    ...style,
                }}
                {...rest}
            >
                <ShimmerOverlay />
            </span>
        );
    }

    // rect (default)
    return (
        <span
            data-testid="aya-skeleton-rect"
            role="presentation"
            aria-hidden="true"
            className={className}
            style={{
                ...baseSurface(radius || "var(--aya-radius-md)"),
                width: dim(width, "100%"),
                height: dim(height, "16px"),
                ...style,
            }}
            {...rest}
        >
            <ShimmerOverlay />
        </span>
    );
}

export default AyaSkeleton;
